import type { JSX, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AppConfig } from "@/common/appConfig";
import { cn } from "@/lib/utils";

export const WORKSPACE_COLUMN_ID = "v3-workspace-column";
const DESKTOP = "(min-width: 1024px)";

/**
 * v3.2 app on desktop (1024px and up, inside V3Shell): New visit and Add
 * recording open in the workspace column (sidebar visible, no scrim) instead
 * of a sheet, as on the iPad. Phones and the classic app keep the sheet.
 */
export const useWorkspaceInline = (): boolean => {
  const query = (): boolean =>
    AppConfig.enableAppRedesign === true &&
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(DESKTOP).matches &&
    !!document.getElementById(WORKSPACE_COLUMN_ID);
  const [inline, setInline] = useState(query);
  useEffect(() => {
    if (
      AppConfig.enableAppRedesign !== true ||
      typeof window.matchMedia !== "function"
    )
      return;
    const media = window.matchMedia(DESKTOP);
    const update = (): void => setInline(query());
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);
  return inline;
};

/**
 * The panel over the workspace column (top bar and page included; the
 * sidebar stays). Escape asks the owner to close; focus moves into it.
 */
export const WorkspacePanel = ({
  open,
  label,
  onEscape,
  children,
  testId,
  wide = false,
}: {
  open: boolean;
  label: string;
  /** Room for a side rail (New visit's Properties) on wide screens. */
  wide?: boolean;
  onEscape?: () => void;
  children: ReactNode;
  testId?: string;
}): JSX.Element | null => {
  const ref = useRef<HTMLElement>(null);
  const target =
    typeof document !== "undefined"
      ? document.getElementById(WORKSPACE_COLUMN_ID)
      : null;
  useEffect(() => {
    if (open) ref.current?.focus();
  }, [open]);
  if (!open || !target) return null;
  return createPortal(
    <section
      ref={ref}
      aria-label={label}
      className="absolute inset-0 z-30 flex min-h-0 flex-col bg-v3-panel font-v3 text-v3-text outline-none"
      data-testid={testId ?? "workspace-panel"}
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === "Escape" && !event.defaultPrevented && onEscape) {
          event.stopPropagation();
          onEscape();
        }
      }}
    >
      <div
        className={cn(
          "mx-auto flex min-h-0 w-full max-w-[720px] flex-1 flex-col",
          wide && "xl:max-w-[1120px]",
        )}
      >
        {children}
      </div>
    </section>,
    target,
  );
};
